import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLazyQuery } from "@apollo/client";
import { searchExternalUsersInit, nlSearchExternalInit, nlSearchExternalClear, SearchValue, fetchCreditsBalanceRequested, fetchUserProfile, toggleSearchTrue } from "../../redux/actions";
import { GIGS_QUERY } from "../../components/PlaceBidModal/gigsQuery";
import { ApolloWrapper } from "../../components/ApolloWrapper/ApolloWrapper";
import "./QueryPage.css";
import QueryPageHeader from "./components/organisms/QueryPageHeader";
import FilterDrawer from "./components/organisms/FilterDrawer";
import NlpSearchModal from "./components/organisms/NlpSearchModal";
import TalentSection from "./components/organisms/TalentSection";
import Pagination from "./components/organisms/Pagination";
import ExternalTalentCard from "./components/organisms/ExternalTalentCard";
import QueryEmptyState from "./components/organisms/QueryEmptyState";
import FeatureGate from "../FeatureGate/FeatureGate";
import Loader from "../../components/Loading/Loader";
import { ExternalTalentIcon } from "./QueryPageIcons";
import {
  INITIAL,
  getActiveFilterCount,
} from "./components/organisms/FilterDrawerPanels/filterOptions";
import { buildExternalFiltersPayload } from "./filterPayloadMappers";
import lockedExternalTalentPlaceholders from "./data/lockedExternalTalentPlaceholders.json";
import BuyCreditsModal from "../../components/BuyCreditsModal/BuyCreditsModal";
import ManageSubscriptionModal from "../../components/ManageSubscriptionModal/ManageSubscriptionModal";
import { canUseSubscriptionFeatures } from "../../shared/billing";
import {
  EXTERNAL_TALENT_LOCK_ACTIONS,
  EXTERNAL_TALENT_LOCK_CONFIG,
  getCreditsBalancePayload,
  getExternalTalentLockState,
} from "./externalTalentLocking";
import {
  buildQueryResultSearch,
  parseQueryResultState,
} from "./queryStateUrl";

const PAGE_SIZE = 20;

// talentFilters as the AI endpoints hand them back (job_title / skills /
// location / years_of_experience) mapped onto our own filter shape.
const buildFiltersFromTalentFilters = (tf) => ({
  ...INITIAL,
  jobTitleInclude: Array.isArray(tf?.job_title) ? tf.job_title : [],
  location: Array.isArray(tf?.location)
    ? tf.location.filter((v) => typeof v === "string" && v.trim())
    : (typeof tf?.location === "string" && tf.location.trim() ? [tf.location] : []),
  skill: Array.isArray(tf?.skills) ? tf.skills : [],
  yearsOfExp: Array.isArray(tf?.years_of_experience)
    ? tf.years_of_experience.map((v) => (typeof v === "string" ? v.replace(/_/g, "-") : v))
    : [],
});

const ExternalTalentResultsPage = () => {
  const { searchValue } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures();

  const reduxProfile = useSelector((state) => state?.profileReducer?.user?.profile);
  const gigsCalledRef = useRef(false);
  const isAiModeInitRef = useRef(true);

  const [filterOpen, setFilterOpen] = useState(false);
  const nlSkillsSyncedRef = useRef("");
  const [nlpModalOpen, setNlpModalOpen] = useState(false);
  const [nlpModalKey, setNlpModalKey] = useState(0);
  const [nlpState, setNlpState] = useState({ description: localStorage.getItem("nlpDescription") || '', talentFilters: null, jobTitles: [] });
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [showManageSubscriptionModal, setShowManageSubscriptionModal] = useState(false);
  const searchTerm = searchValue ? decodeURIComponent(searchValue) : "RN Med Surg";

  const [queryState, setQueryState] = useState(() => {
    const base = parseQueryResultState(location.search);
    try {
      const saved = localStorage.getItem("aiTalentFilters");
      if (saved) {
        const tf = JSON.parse(saved);
        if (!tf) return base;
        return {
          ...base,
          filters: buildFiltersFromTalentFilters(tf),
          hasAppliedFilters: true,
        };
      }
    } catch (e) {
      localStorage.removeItem("aiTalentFilters");
    }
    return base;
  });

  const [draftFilters, setDraftFilters] = useState(queryState.filters);
  const {
    filters,
    sortOrder,
    currentPage,
    hasAppliedFilters,
  } = queryState;
  const {
    data: talents,
    loading,
    displayedTotalCount: rawTotalCount,
    pageSize: rawPageSize,
  } = useSelector((state) => state.searchExternalUsersReducer);
  const safeTalents = Array.isArray(talents) ? talents : [];

  const {
    isActive: isAiMode,
    data: nlExternalTalents,
    loading: nlExternalLoading,
    displayedTotalCount: nlExternalTotalCount,
    pageSize: nlPageSize,
    errorType: nlErrorType,
    appliedFilters: nlAppliedFilters,
  } = useSelector((state) => state.nlSearchExternalReducer);

  const displayedTalentsRaw = isAiMode ? (nlExternalTalents || []) : safeTalents;
  const displayedLoading = isAiMode ? nlExternalLoading : loading;
  const displayedTotalCount = isAiMode
    ? nlExternalTotalCount || (nlExternalTalents?.length ?? 0)
    : rawTotalCount || safeTalents.length;
  const {
    creditsBalance,
    creditsBalanceLoaded,
    plans,
    loading: plansLoading,
  } = useSelector((state) => state.plansReducer);
  const creditsBalancePayload = getCreditsBalancePayload(creditsBalance);
  const availableCredits = Number(creditsBalancePayload?.totalCreditsBalance) || 0;
  const externalTalentLockState = subscriptionFeaturesEnabled ? getExternalTalentLockState({
    isAiMode,
    creditsBalanceLoaded,
    creditsBalancePayload,
    availableCredits,
  }) || (nlErrorType === 'BADREQUEST' ? 'creditsEnded' : null) : null;
  const externalTalentLocked = Boolean(externalTalentLockState);
  const externalTalentLockConfig =
    EXTERNAL_TALENT_LOCK_CONFIG[externalTalentLockState] || {};
  const handleExternalTalentLockedAction = () => {
    if (!subscriptionFeaturesEnabled) return;

    if (
      externalTalentLockConfig.actionType ===
      EXTERNAL_TALENT_LOCK_ACTIONS.BUY_CREDITS
    ) {
      setShowBuyCreditsModal(true);
      return;
    }

    setShowManageSubscriptionModal(true);
  };
  const displayedTalents = externalTalentLocked
    ? lockedExternalTalentPlaceholders
    : displayedTalentsRaw;

  const updateQueryState = (nextState = {}) => {
    const nextSearch = buildQueryResultSearch({
      filters: nextState.filters ?? filters,
      sortOrder: nextState.sortOrder ?? sortOrder,
      currentPage: nextState.currentPage ?? currentPage,
    });
    const nextQueryState = parseQueryResultState(nextSearch);

    if (nextSearch !== location.search) {
      history.push({
        pathname: location.pathname,
        search: nextSearch,
      });
    }

    setQueryState(nextQueryState);
    return nextQueryState;
  };

  const fetchExternalTalents = (
    page,
    nextFilters,
    nextSortOrder,
    shouldApplyFilters
  ) => {
    if (isAiMode) {
      const filterPayload = shouldApplyFilters
        ? buildExternalFiltersPayload(nextFilters, page)
        : {};
      if (!filterPayload.job_title?.length) {
        filterPayload.job_title = [searchTerm];
      }
      dispatch(nlSearchExternalInit({ query: nlpState.description || searchTerm, page, ...filterPayload }));
      return;
    }

    const payload = {
      search: searchTerm,
      perPage: PAGE_SIZE,
      page,
      sortOrder: nextSortOrder,
    };

    if (shouldApplyFilters) {
      Object.assign(
        payload,
        buildExternalFiltersPayload(
          nextFilters,
          page,
          nextSortOrder
        )
      );
    }

    dispatch(searchExternalUsersInit(payload));
  };

  const handleResetFilters = () => {
    try {
      localStorage.removeItem("aiTalentFilters");
      localStorage.removeItem("nlpDescription");
      localStorage.removeItem("nlpJobLocation");
      setDraftFilters(INITIAL);
      setFilterOpen(false);
      setNlpState({ description: '', talentFilters: null, jobTitles: [] });
      setNlpModalKey((k) => k + 1);
      dispatch(toggleSearchTrue());
      dispatch(nlSearchExternalClear());
      updateQueryState({ filters: INITIAL, currentPage: 1 });
      dispatch(searchExternalUsersInit({ search: searchTerm, perPage: PAGE_SIZE, page: 1 }));
    } catch (error) {
      console.error("Failed to reset filters:", error);
    }
  };

  const [getGigs] = useLazyQuery(GIGS_QUERY, { fetchPolicy: "no-cache" });

  useEffect(() => {
    const profileId = reduxProfile?.ProfileId;
    if (!profileId || gigsCalledRef.current) return;
    gigsCalledRef.current = true;
    getGigs({
      variables: {
        MygigsProfileId: profileId,
        all: "",
        isPrivateGig: false,
        _page: "1",
        _perPage: 21,
        _sort: "MOST_RECENT",
        JobOrderStatusName: "All",
      },
    });
  }, [reduxProfile?.ProfileId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(fetchUserProfile());
    if (subscriptionFeaturesEnabled) {
      dispatch(fetchCreditsBalanceRequested());
    }
  }, [subscriptionFeaturesEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      localStorage.setItem("queryPage_state", JSON.stringify({ filters: queryState.filters, currentPage: queryState.currentPage, sortOrder }));
    } catch (e) {}
  }, [queryState.filters, queryState.currentPage, sortOrder]);

  useEffect(() => {
    const isSearchDomain = (p) => p.startsWith('/external/') || p === '/external' || p.startsWith('/query/') || p === '/query';
    return () => {
      if (!isSearchDomain(history.location.pathname)) {
        try {
          localStorage.removeItem("aiTalentFilters");
          localStorage.removeItem("nlpDescription");
          localStorage.removeItem("nlpJobLocation");
          localStorage.removeItem("queryPage_state");
        } catch (e) {}
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchTerm) dispatch(SearchValue(searchTerm));
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAiModeInitRef.current) {
      isAiModeInitRef.current = false;
      return;
    }
    if (!isAiMode) {
      fetchExternalTalents(1, filters, sortOrder, hasAppliedFilters);
    }
  }, [isAiMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply the skills that NL search resolved from the prompt onto the filter state
  useEffect(() => {
    const nlSkills = Array.isArray(nlAppliedFilters?.skills)
      ? nlAppliedFilters.skills.filter((skill) => typeof skill === "string" && skill.trim())
      : [];
    if (!nlSkills.length) {
      nlSkillsSyncedRef.current = "";
      return;
    }
    const signature = nlSkills.join("|");
    if (nlSkillsSyncedRef.current === signature) return;
    nlSkillsSyncedRef.current = signature;
    setQueryState((prev) =>
      prev.filters?.skill?.length
        ? prev
        : { ...prev, filters: { ...prev.filters, skill: nlSkills } }
    );
    setDraftFilters((prev) => (prev?.skill?.length ? prev : { ...prev, skill: nlSkills }));
  }, [nlAppliedFilters]);

  useEffect(() => {
    const alreadyHasResults =
      (nlExternalTalents || []).length > 0 || safeTalents.length > 0;
    const alreadySearching = (isAiMode && nlExternalLoading) || (!isAiMode && loading);
    if (alreadyHasResults || alreadySearching) return;

    dispatch(nlSearchExternalClear());
    if (hasAppliedFilters) {
      const filterPayload = buildExternalFiltersPayload(filters, currentPage);
      if (isAiMode) {
        dispatch(nlSearchExternalInit({ query: searchTerm, ...filterPayload }));
      } else {
        dispatch(searchExternalUsersInit({ search: searchTerm, perPage: PAGE_SIZE, ...filterPayload }));
      }
    } else {
      const aiFiltersStr = localStorage.getItem("aiTalentFilters");
      if (aiFiltersStr) {
        try {
          const tf = JSON.parse(aiFiltersStr);
          const filterPayload = buildExternalFiltersPayload({
            jobTitleInclude: Array.isArray(tf?.job_title) ? tf.job_title : [],
            location: Array.isArray(tf?.location) ? tf.location : [],
            skill: Array.isArray(tf?.skills) ? tf.skills : [],
            yearsOfExp: Array.isArray(tf?.years_of_experience) ? tf.years_of_experience.map((v) => (typeof v === "string" ? v.replace(/_/g, "-") : v)) : [],
          }, currentPage);
          dispatch(searchExternalUsersInit({ search: searchTerm, perPage: PAGE_SIZE, ...filterPayload }));
        } catch {
          dispatch(searchExternalUsersInit({ search: searchTerm, perPage: PAGE_SIZE, page: currentPage }));
        }
      } else {
        const nlpDescription = localStorage.getItem("nlpDescription");
        const nlpJobLocation = localStorage.getItem("nlpJobLocation");
        const baseQuery = nlpDescription || searchTerm;
        const queryWithLocation = nlpJobLocation ? `${baseQuery} and in ${nlpJobLocation.split(",")[0]}` : baseQuery;
        const nlFilters = { ...INITIAL, jobTitleInclude: [searchTerm] };
        try {
          localStorage.setItem("queryPage_state", JSON.stringify({ filters: nlFilters, currentPage, sortOrder }));
        } catch (e) {}
        dispatch(nlSearchExternalInit({
          query: queryWithLocation,
          job_title: [searchTerm],
          ...(nlpJobLocation ? { location: [nlpJobLocation] } : {}),
          page: currentPage,
        }));
      }
    }
  }, []);

  const openFilterDrawer = () => {
    const draft = { ...filters };
    if (isAiMode && !draft.jobTitleInclude?.length) {
      draft.jobTitleInclude = [searchTerm];
    }
    if (isAiMode && !draft.location?.length) {
      const nlpJobLocation = localStorage.getItem("nlpJobLocation");
      if (nlpJobLocation) draft.location = [nlpJobLocation];
    }
    setDraftFilters(draft);
    setFilterOpen(true);
  };

  const handleNlpSearch = ({ jobTitles, description, talentFilters, aiGenerated }) => {
    setNlpState({ description, talentFilters, jobTitles: jobTitles || [], aiGenerated });
    // A "Write with AI" description goes to the plain search endpoint with the
    // very same payload nl_search used to receive. Clearing the nl slice drops
    // isAiMode too, so later filter/page fetches stay on plain search as well.
    const runSearch = aiGenerated ? searchExternalUsersInit : nlSearchExternalInit;
    if (aiGenerated) {
      dispatch(nlSearchExternalClear());
      // write-with-ai already returned the filters it resolved, so apply those
      // instead of waiting on an nl-search response that will never come.
      if (talentFilters) {
        const aiFilters = buildFiltersFromTalentFilters(talentFilters);
        setDraftFilters(aiFilters);
        updateQueryState({ filters: aiFilters, currentPage: 1 });
        const titles = aiFilters.jobTitleInclude;
        if (titles.length > 0) dispatch(SearchValue(titles.join(', ')));
        dispatch(
          searchExternalUsersInit({
            query: description || titles.join(', '),
            perPage: PAGE_SIZE,
            ...buildExternalFiltersPayload(aiFilters, 1),
          })
        );
        return;
      }
    }
    if (jobTitles?.length > 0) {
      const nextFilters = { ...INITIAL, jobTitleInclude: jobTitles };
      setDraftFilters(nextFilters);
      updateQueryState({ filters: nextFilters, currentPage: 1 });
      dispatch(SearchValue(jobTitles.join(', ')));
      dispatch(runSearch({ query: description || jobTitles.join(', '), page: 1, job_title: jobTitles }));
    } else if (description) {
      dispatch(runSearch({ query: description, page: 1 }));
    }
  };

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
      return;
    }

    history.push(`/query/${encodeURIComponent(searchTerm)}`);
  };

  const effectivePageSize = isAiMode ? (nlPageSize || PAGE_SIZE) : (rawPageSize || PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(displayedTotalCount / effectivePageSize));

  const planStatus = creditsBalance?.data?.status || '';
  const isExpiredPlan = subscriptionFeaturesEnabled && planStatus === 'expired';

  if (subscriptionFeaturesEnabled && !creditsBalanceLoaded) return <Loader />;

  if (isExpiredPlan || (subscriptionFeaturesEnabled && nlErrorType === 'FORBIDDEN')) return (
    <FeatureGate
      featureName="Skill Radar"
      iconType="search"
      description="Your free trial has ended. Choose a plan to continue accessing 850M+ professional profiles and keep finding the right talent for your open roles."
      bottomText="Skill Radar is a paid feature and was given to you for free during your trial period."
    />
  );

  if (displayedLoading) return <Loader />;

  return (
    <div className="qp-container">
      <QueryPageHeader
        totalMatches={externalTalentLocked ? 0 : displayedTotalCount}
        searchTerm={nlpState.jobTitles?.length ? nlpState.jobTitles.join(', ') : searchTerm}
        sortOrder={sortOrder}
        onChangeSortOrder={subscriptionFeaturesEnabled ? undefined : (nextSortOrder) => {
          const nextState = updateQueryState({
            sortOrder: nextSortOrder,
            currentPage: 1,
          });
          fetchExternalTalents(
            nextState.currentPage,
            nextState.filters,
            nextState.sortOrder,
            nextState.hasAppliedFilters
          );
        }}
        filterCount={getActiveFilterCount(isAiMode && !filters.jobTitleInclude?.length ? { ...filters, jobTitleInclude: [searchTerm] } : filters) + (subscriptionFeaturesEnabled && nlpState.description ? 1 : 0)}
        onOpenFilter={openFilterDrawer}
      />

      {subscriptionFeaturesEnabled && (
        <NlpSearchModal
          key={nlpModalKey}
          open={nlpModalOpen}
          onClose={() => setNlpModalOpen(false)}
          onSearch={handleNlpSearch}
          hideTitleField={nlpState.jobTitles?.length > 0 || !!nlpState.description}
          initialJobTitles={nlpState.jobTitles || []}
          initialDescription={nlpState.description || ''}
        />
      )}

      <FilterDrawer
        open={filterOpen}
        values={draftFilters}
        onChange={setDraftFilters}
        onClose={() => setFilterOpen(false)}
        onNlpClick={subscriptionFeaturesEnabled ? () => setNlpModalOpen(true) : undefined}
        nlpState={subscriptionFeaturesEnabled ? nlpState : undefined}
        onNlpEdit={subscriptionFeaturesEnabled ? () => setNlpModalOpen(true) : undefined}
        onNlpClear={subscriptionFeaturesEnabled ? () => {
          setNlpState((prev) => ({ ...prev, description: '' }));
          setNlpModalKey((k) => k + 1);
        } : undefined}
        onReset={handleResetFilters}
        onApply={(nextFilters) => {
          setFilterOpen(false);
          const appliedTitles = nextFilters?.jobTitleInclude || [];
          setNlpState((prev) => ({ ...prev, jobTitles: appliedTitles }));
          if (appliedTitles.length > 0) {
            dispatch(SearchValue(appliedTitles.join(', ')));
          }
          setNlpModalKey((k) => k + 1);
          try { localStorage.setItem("queryPage_state", JSON.stringify({ filters: nextFilters || INITIAL, currentPage: 1, sortOrder })); } catch (e) {}
          const nextState = updateQueryState({
            filters: nextFilters || INITIAL,
            currentPage: 1,
          });
          if (appliedTitles.length > 0) {
            const newPathname = `/external/${encodeURIComponent(appliedTitles[0])}`;
            if (location.pathname !== newPathname) {
              history.replace({ pathname: newPathname, search: history.location.search });
            }
          }
          fetchExternalTalents(
            nextState.currentPage,
            nextState.filters,
            nextState.sortOrder,
            nextState.hasAppliedFilters
          );
        }}
      />

      <TalentSection
        iconEmoji={<ExternalTalentIcon />}
        iconClass="qp-icon-external"
        titleText="Skillradar Talent"
        count={externalTalentLocked ? 0 : displayedTotalCount}
        locked={externalTalentLocked}
        lockedTitle={externalTalentLockConfig.title}
        lockedDescription={externalTalentLockConfig.description}
        lockedActionLabel={externalTalentLockConfig.actionLabel}
        onLockedAction={handleExternalTalentLockedAction}
      >
        {displayedTalents.length === 0 ? (
          <QueryEmptyState
            onAction={handleResetFilters}
            searchQuery={searchTerm}
            talentType="external"
          />
        ) : (
          displayedTalents.map((talent) => (
            <ExternalTalentCard
              key={talent.id || talent._id}
              talent={talent}
              profileUrl={`/external-detail/${talent.id || talent._id}`}
              onViewProfile={(id) => history.push(`/external-detail/${id}`)}
            />
          ))
        )}
      </TalentSection>

      {subscriptionFeaturesEnabled && <BuyCreditsModal
        visible={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        onUpgradeToPlan={() => setShowManageSubscriptionModal(true)}
        availableCredits={availableCredits}
      />}
      {subscriptionFeaturesEnabled && <ManageSubscriptionModal
        visible={showManageSubscriptionModal}
        onClose={() => setShowManageSubscriptionModal(false)}
        plans={plans}
        creditsBalance={creditsBalance}
        loading={plansLoading}
      />}
      {!externalTalentLocked && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => {
            const nextState = updateQueryState({ currentPage: page });
            fetchExternalTalents(
              nextState.currentPage,
              nextState.filters,
              nextState.sortOrder,
              nextState.hasAppliedFilters
            );
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
    </div>
  );
};

const ExternalTalentResultsPageWithApollo = (props) => (
  <ApolloWrapper>
    <ExternalTalentResultsPage {...props} />
  </ApolloWrapper>
);

export default ExternalTalentResultsPageWithApollo;
